<template>
  <FullScreenLayoutContent>
    <div class="flex grow flex-col items-center justify-center p-6">
      <div class="relative flex flex-col items-center justify-center">
        <h1
          class="md:tex relative z-10 text-center text-gray-700 transition-all delay-50 duration-200"
          :class="[
            step === -1 && 'md:text-display-large text-display-medium',
            step > -1 && 'text-display-small',
          ]"
        >
          Willkommen bei Menon!
        </h1>
        <p class="text-body-large text-on-surface-variant mt-1 mb-4 text-center md:text-left">
          Du bist Administrator:in deiner Organisation. Lass uns deine Organisation aufsetzen.
        </p>
        <BaseTextButton
          v-if="step === -1"
          color="filled"
          size="L"
          @click="step = 0"
          class="z-10 mt-10"
          trailing-icon="start"
          >{{ $t('common.start') }}
        </BaseTextButton>
        <BaseShape
          v-if="step === -1"
          shape="flower"
          class="text-primary-container animate-rotate absolute -top-[10vw] -left-[10vw] z-0 h-[15vw] min-h-[150px] w-[15vw] min-w-[150px] transition-all"
        />
        <BaseShape
          v-if="step === -1"
          shape="soft-boom"
          class="text-tertiary-container absolute -right-[7vw] -bottom-[7vw] z-0 h-[12vw] min-h-[120px] w-[12vw] min-w-[120px] transition-all"
        />
      </div>
      <template v-if="organizationLoadingStatus === ApiRequestStatus.Success">
        <BaseAnimatedExtend :is-extended="step >= 0" :delay="0" :duration="600">
          <ul class="flex w-full flex-col gap-3">
            <li>
              <OnboardingStep
                :step="1"
                :is-active="step === 0"
                :is-completed="step > 0"
                title="Name deiner Organisation"
                :description="organizationNameDescription"
              >
                <BaseTextButton v-if="step > 0" @click="step = 0">{{
                  $t('common.edit')
                }}</BaseTextButton>
                <template #actions>
                  <BaseTextButton
                    class="text-on-surface-variant!"
                    color="text"
                    size="M"
                    @click="openOrganizationNameDialog"
                  >
                    Namen anpassen
                  </BaseTextButton>
                  <BaseTextButton
                    color="filled"
                    size="M"
                    @click="nextStep"
                    trailing-icon="arrow_forward"
                  >
                    {{ $t('common.next') }}
                  </BaseTextButton>
                </template>
              </OnboardingStep>
            </li>
            <li>
              <OnboardingStep
                :step="2"
                title="Welche Rollen nimmst du selber ein?"
                description="Mehrfachauswahl - du kannst die Auswahl später jederzeit ändern."
                :is-active="step === 1"
                :is-completed="step > 1"
              >
                <BaseTextButton v-if="step > 1" @click="step = 1">{{
                  $t('common.edit')
                }}</BaseTextButton>
                <template #bottom>
                  <BaseButtonGroup size="M" class="mb-4 ml-20">
                    <BaseTextButton
                      v-for="role in organizationData.availableRoles"
                      :key="role"
                      :description="descriptionFor(role)"
                      color="tonal"
                      size="S"
                      :icon="selectedOwnRoles.includes(role) ? 'check' : undefined"
                      :selected="selectedOwnRoles.includes(role)"
                      :disabled="role === Role.ADMIN"
                      @click="toggleOwnRole(role)"
                      >{{ $t(`organization.roles.${role}`) }}</BaseTextButton
                    >
                  </BaseButtonGroup>
                </template>
                <template #actions>
                  <BaseTextButton color="text" size="M" icon="help" @click="openMyRolesDialog">
                    Bedeutung der Rollen</BaseTextButton
                  >
                  <BaseTextButton
                    color="filled"
                    size="M"
                    @click="saveMyRoles"
                    trailing-icon="arrow_forward"
                    class="ml-auto"
                  >
                    {{ $t('common.next') }}
                  </BaseTextButton>
                </template>
              </OnboardingStep>
            </li>
            <li>
              <OnboardingStep
                :step="3"
                :is-active="step === 2"
                :is-completed="step > 2"
                title="Wer soll noch in deine Organisation eingeladen werden?"
                description="Lade Mitglieder ein und weise ihnen Rollen zu."
              >
                <BaseTextButton v-if="step > 2" @click="step = 2">{{
                  $t('common.edit')
                }}</BaseTextButton>
                <template #bottom>
                  <ul class="flex flex-col gap-0.5">
                    <li
                      v-for="member in organizationData.members.filter(
                        (member) => member.id !== user.id,
                      )"
                      :key="member.id"
                      class="bg-surface-container rounded-small p-2 pr-2 pl-6"
                    >
                      <MemberListItem
                        :member="member"
                        :availableRoles="organizationData.availableRoles"
                        @edited-roles="(roles) => editedRoles(member.id, roles)"
                        @deleted="deleteMember(member.id)"
                      />
                    </li>
                  </ul>
                  <div class="w-full px-6 py-4">
                    <BaseTextButton
                      color="tonalPrimary"
                      size="M"
                      class="w-full"
                      @click="openAddMemberDialog"
                      icon="person_add"
                    >
                      Mitglieder hinzufügen
                    </BaseTextButton>
                  </div>
                </template>
                <template #actions>
                  <BaseTextButton
                    color="filled"
                    size="M"
                    class="ml-auto"
                    @click="nextStep"
                    trailing-icon="arrow_forward"
                  >
                    {{ $t('common.next') }}
                  </BaseTextButton>
                </template>
              </OnboardingStep>
            </li>
            <li>
              <OnboardingStep
                :step="4"
                :is-active="step === 3"
                :is-completed="step > 3"
                :title="$t('settings.feedbackTopics.title')"
                description="Wähle aus, welche Themen in deiner Organisation verfügbar sein sollen. Du kannst die Auswahl später jederzeit ändern."
              >
                <template #bottom>
                  <LoadingWrapper :state="organizationFeedbackTopicsStatus">
                    <div class="flex flex-col gap-0.5 px-6 pb-2">
                      <ManageFeedbackTopicItem
                        v-for="feedbackTopic in feedbackTopics"
                        :key="feedbackTopic.id"
                        :feedback-topic="feedbackTopic"
                        :disable-confirmation="true"
                        class="bg-surface!"
                        @feedback-topic-enabled-updated="onFeedbackTopicEnabledUpdated"
                      />
                    </div>
                  </LoadingWrapper>
                </template>
                <template #actions>
                  <BaseTextButton
                    color="filled"
                    size="M"
                    class="ml-auto"
                    icon="start"
                    @click="finishOnboarding"
                  >
                    Weiter zur App
                  </BaseTextButton>
                </template>
              </OnboardingStep>
            </li>
          </ul>
        </BaseAnimatedExtend>
        <BaseDialog
          type="basic"
          :is-open="isEditOrganizationDialogOpen"
          title="Organisation benennen"
          class="md:w-[440px]"
          @close="closeEditOrganizationDialog"
        >
          <BaseInputField
            v-model="organizationName"
            :label="$t('organization.title')"
            :title="$t('organization.title')"
            type="text"
            required
            class="bg-surface-container-high"
          />
          <template #dismiss>
            <BaseTextButton @click="closeEditOrganizationDialog">{{
              $t('common.cancel')
            }}</BaseTextButton>
          </template>
          <template #confirm>
            <BaseTextButton color="filled" @click="saveOrganizationName">{{
              $t('common.save')
            }}</BaseTextButton>
          </template>
        </BaseDialog>

        <BaseDialog
          type="basic"
          :is-open="isEditMyRolesDialogOpen"
          title="Bedeutung der Rollen"
          class="md:w-[440px]"
          @close="closeEditMyRolesDialog"
        >
          <div class="flex flex-col gap-2">
            <div
              v-for="role in organizationData.availableRoles"
              :key="role"
              class="rounded-extra-small! first:rounded-t-medium! last:rounded-b-medium!"
            >
              <h3 class="text-title-medium">{{ $t(`organization.roles.${role}`) }}</h3>
              <p class="text-body-medium text-on-surface-variant">
                {{ descriptionFor(role) }}
              </p>
            </div>
          </div>
          <template #dismiss>
            <BaseTextButton @click="closeEditMyRolesDialog">{{
              $t('common.close')
            }}</BaseTextButton>
          </template>
        </BaseDialog>

        <InviteMemberDialog
          :is-open="isAddMemberDialogOpen"
          :available-roles="organizationData.availableRoles"
          @member-added="addMember"
          @close="closeAddMemberDialog"
        />
      </template>
    </div>
  </FullScreenLayoutContent>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import FullScreenLayoutContent from '@/core/components/layouts/FullScreenLayoutContent.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import { useUserStore } from '@/stores/user.store'
  import { Role, toggleOrganizationRole } from '@/modules/organization/models/organization-role'
  import InviteMemberDialog from '@/modules/settings/components/InviteMemberDialog.vue'
  import MemberListItem from '@/modules/settings/components/MemberListItem.vue'
  import ManageFeedbackTopicItem from '@/modules/settings/components/ManageFeedbackTopicItem.vue'
  import { useOrganization } from '@/modules/settings/composables/useOrganization'
  import { useOrganizationSettings } from '@/modules/settings/composables/useOrganizationSettings'
  import { useOrganizationFeedbackTopics } from '@/modules/settings/composables/use-organization-feedback-topics'
  import {
    roleDescriptionKey,
    useOrganizationCapabilitySettings,
  } from '@/modules/settings/composables/use-organization-capability-settings'
  import { useI18n } from 'vue-i18n'
  import OnboardingStep from '../components/OnboardingStep.vue'
  import BaseButtonGroup from '@/core/components/base/button/BaseButtonGroup.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import usePut from '@/core/composables/api/usePut'
  import { useRouter } from 'vue-router'
  import { useOrganizationStore } from '@/stores/organization.store'
  import BaseAnimatedExtend from '@/core/components/base/animated-extend/BaseAnimatedExtend.vue'
  import BaseShape from '@/core/components/base/shapes/BaseShape.vue'

  const { t } = useI18n()
  const { user } = useUserStore()
  const { settings: capabilitySettings } = useOrganizationCapabilitySettings()

  const descriptionFor = (role: Role) => t(roleDescriptionKey(role, capabilitySettings.value))
  const { data: organizationData, status: organizationLoadingStatus } = useOrganization()
  const {
    updateOrganizationName,
    mutationStatus,
    editedRoles,
    deleteMember,
    isAddMemberDialogOpen,
    openAddMemberDialog,
    closeAddMemberDialog,
    addMember,
  } = useOrganizationSettings(organizationData)
  const {
    status: organizationFeedbackTopicsStatus,
    feedbackTopics,
    setFeedbackTopicEnabled,
  } = useOrganizationFeedbackTopics()
  const {
    isOpen: isEditOrganizationDialogOpen,
    openDialog: openEditOrganizationDialog,
    closeDialog: closeEditOrganizationDialog,
  } = useDialog()

  const {
    isOpen: isEditMyRolesDialogOpen,
    openDialog: openEditMyRolesDialog,
    closeDialog: closeEditMyRolesDialog,
  } = useDialog()

  const organizationName = ref('')

  const currentUserMember = computed(() =>
    organizationData.value?.members.find((member) => member.id === user.id),
  )

  watch(currentUserMember, () => {
    selectedOwnRoles.value = [...(currentUserMember.value?.roles ?? [])]
  })

  const selectedOwnRoles = ref<Role[]>([...(currentUserMember.value?.roles ?? [])])

  const organizationNameDescription = computed(() => organizationData.value.name)

  const openOrganizationNameDialog = () => {
    organizationName.value = organizationData.value.name
    openEditOrganizationDialog()
  }

  const saveOrganizationName = async () => {
    const trimmedOrganizationName = organizationName.value.trim()
    if (!trimmedOrganizationName) return

    organizationData.value.name = trimmedOrganizationName
    await updateOrganizationName()
    if (mutationStatus.value === ApiRequestStatus.Success) {
      closeEditOrganizationDialog()
    }
  }

  const openMyRolesDialog = () => {
    selectedOwnRoles.value = [...(currentUserMember.value?.roles ?? [])]
    openEditMyRolesDialog()
  }

  const toggleOwnRole = (role: Role) => {
    selectedOwnRoles.value = toggleOrganizationRole(selectedOwnRoles.value, role)
  }

  const saveMyRoles = async () => {
    if (!currentUserMember.value) return
    await editedRoles(currentUserMember.value.id, selectedOwnRoles.value)
    nextStep()
  }

  const step = ref<-1 | 0 | 1 | 2 | 3>(-1)

  const nextStep = () => {
    step.value += 1
  }

  const onFeedbackTopicEnabledUpdated = async (input: {
    feedbackTopicId: string
    enabled: boolean
  }) => {
    await setFeedbackTopicEnabled(input.feedbackTopicId, input.enabled)
  }

  const { mutate: mutateFinishOnboarding, status: statusFinishOnboarding } = usePut<void, void>(
    'organization/onboarding/complete',
  )

  const router = useRouter()
  const organizationStore = useOrganizationStore()

  const finishOnboarding = async () => {
    await mutateFinishOnboarding()
    if (statusFinishOnboarding.value === ApiRequestStatus.Success) {
      await organizationStore.initializeOrganizations()
      router.replace({ name: 'dashboard' })
    }
  }
</script>
